import { AgGridTable, AgGridTableRef } from '@shared/components/ui/AgGridTable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Rate } from '../../models/rate.types';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { RatesSelectionToolbar } from '../interaction/RatesSelectionToolbar';

interface RatesTableProps {
  rates: Rate[];
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  onDeactivate: (rate: Rate) => void;
  canManage: boolean;
  selectedRateIds?: Set<number>;
  onSelectionChanged?: (selectedIds: Set<number>) => void;
  onClearSelection?: () => void;
  onGeneratePdf?: () => void;
  isGeneratingPdf?: boolean;
}

export const RatesTable = ({
  rates,
  isFetching = false,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  onDeactivate,
  canManage,
  selectedRateIds = new Set(),
  onSelectionChanged,
  onClearSelection,
  onGeneratePdf,
  isGeneratingPdf = false,
}: RatesTableProps) => {
  const agGridRef = useRef<AgGridTableRef>(null);
  const [localSelectedCount, setLocalSelectedCount] = useState(0);

  const { columnDefs } = useAgGridColumns({
    onDeactivate,
    canManage,
  });

  const clearSelectionUI = useCallback(() => {
    agGridRef.current?.deselectAll();
    setLocalSelectedCount(0);
  }, []);

  const handleSelectionChanged = useCallback(
    (selectedRows: Rate[]) => {
      const selectedIds = new Set(selectedRows.map((row) => row.id));
      setLocalSelectedCount(selectedRows.length);
      onSelectionChanged?.(selectedIds);
    },
    [onSelectionChanged],
  );

  useEffect(() => {
    if (selectedRateIds.size === 0) {
      clearSelectionUI();
    }
  }, [selectedRateIds, clearSelectionUI]);

  const handleCloseSelectionToolbar = useCallback(() => {
    clearSelectionUI();
    onClearSelection?.();
  }, [clearSelectionUI, onClearSelection]);

  const toolbarCount = localSelectedCount || selectedRateIds.size;

  return (
    <>
      <div
        className={`w-full transition-opacity duration-300 ${
          isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : ''
        }`}
      >
        <AgGridTable
          ref={agGridRef}
          data={rates}
          columnDefs={columnDefs}
          enableSelection={true}
          onSelectionChanged={handleSelectionChanged}
          className="rounded-lg shadow-sm"
          height="calc(100vh - 250px)"
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          gridOptions={{
            getRowId: (params) => String((params.data as Rate)?.id ?? ''),
          }}
        />
      </div>

      <RatesSelectionToolbar
        selectedCount={toolbarCount}
        isGenerating={isGeneratingPdf}
        onClose={handleCloseSelectionToolbar}
        onGeneratePdf={() => onGeneratePdf?.()}
      />
    </>
  );
};
